import {
  ShieldCheck, Clock, CheckCircle, XCircle, GitCompare, FileText, Users,
  ClipboardList, Settings, BarChart3, LayoutDashboard, UserCheck, FilePlus,
} from 'lucide-react';
import { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';

export const ENTITY_TYPES = [
  { value: 'purchase_order', labelKey: 'entity_purchase_order' },
  { value: 'leave', labelKey: 'entity_leave' },
  { value: 'invoice', labelKey: 'entity_invoice' },
  { value: 'expense', labelKey: 'entity_expense' },
  { value: 'contract', labelKey: 'entity_contract' },
];

export const WORKFLOW_STATUS = [
  { value: 'actif', labelKey: 'status_active' },
  { value: 'inactif', labelKey: 'status_inactive' },
  { value: 'brouillon', labelKey: 'status_draft' },
];

export const REQUEST_STATUS = [
  { value: 'en_attente', labelKey: 'req_pending' },
  { value: 'en_cours', labelKey: 'req_in_progress' },
  { value: 'approuve', labelKey: 'req_approved' },
  { value: 'rejete', labelKey: 'req_rejected' },
  { value: 'annule', labelKey: 'req_cancelled' },
];

export const PRIORITY_OPTIONS = [
  { value: 'basse', labelKey: 'priority_low' },
  { value: 'normal', labelKey: 'priority_normal' },
  { value: 'haute', labelKey: 'priority_high' },
  { value: 'urgente', labelKey: 'priority_urgent' },
];

const STATUS_LABEL_FALLBACK = {
  status_active: 'نشط',
  status_inactive: 'غير نشط',
  status_draft: 'مسودة',
  req_pending: 'قيد الانتظار',
  req_in_progress: 'قيد المعالجة',
  req_approved: 'موافق عليه',
  req_rejected: 'مرفوض',
  req_cancelled: 'ملغى',
};

const ENTITY_LABEL_FALLBACK = {
  entity_purchase_order: 'أمر شراء',
  entity_leave: 'إجازة',
  entity_invoice: 'فاتورة',
  entity_expense: 'مصروف',
  entity_contract: 'عقد',
};

const STATUS_VALUE_ALIASES = {
  active: 'actif',
  inactive: 'inactif',
  draft: 'brouillon',
  pending: 'en_attente',
  in_progress: 'en_cours',
  approved: 'approuve',
  rejected: 'rejete',
  cancelled: 'annule',
};

/** DB/API sometimes stores i18n key paths or English slugs instead of canonical values. */
export function normalizeStatusValue(rawStatus) {
  if (!rawStatus) return '';
  const text = String(rawStatus).trim();
  if (!text) return '';

  const tail = text.includes('.') ? text.split('.').pop() : text;
  const fromKey = STATUS_LABEL_FALLBACK[tail] ? tail.replace(/^status_/, '').replace(/^req_/, '') : null;
  if (fromKey && STATUS_VALUE_ALIASES[fromKey]) return STATUS_VALUE_ALIASES[fromKey];
  if (tail.startsWith('status_')) {
    const mapped = tail.replace('status_', '');
    if (mapped === 'active') return 'actif';
    if (mapped === 'inactive') return 'inactif';
    if (mapped === 'draft') return 'brouillon';
  }

  const lower = text.toLowerCase();
  if (STATUS_VALUE_ALIASES[lower]) return STATUS_VALUE_ALIASES[lower];
  if ([...WORKFLOW_STATUS, ...REQUEST_STATUS].some((s) => s.value === lower)) return lower;
  return text;
}

function safeLabel(labelFn, labelKey, statusFallback = '') {
  if (!labelKey) return statusFallback;
  const raw = labelFn(labelKey, { defaultValue: STATUS_LABEL_FALLBACK[labelKey] || ENTITY_LABEL_FALLBACK[labelKey] || '' });
  const text = String(raw ?? '').trim();
  if (!text || text.startsWith('modules.') || text.includes('.pages.')) {
    return STATUS_LABEL_FALLBACK[labelKey] || ENTITY_LABEL_FALLBACK[labelKey] || statusFallback;
  }
  return text;
}

export function entityLabel(entityType, labelFn) {
  if (!entityType) return '—';
  const hit = ENTITY_TYPES.find((e) => e.value === entityType);
  return hit ? safeLabel(labelFn, hit.labelKey, entityType) : entityType;
}

export function statusBadge(status, labelFn) {
  const normalized = normalizeStatusValue(status);
  const colors = {
    actif: 'bg-green-500/20 text-green-300 border-green-500/30',
    inactif: 'bg-red-500/20 text-red-300 border-red-500/30',
    brouillon: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    en_attente: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    en_cours: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    approuve: 'bg-green-500/20 text-green-300 border-green-500/30',
    rejete: 'bg-red-500/20 text-red-300 border-red-500/30',
    annule: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  };
  const labelKey = [...WORKFLOW_STATUS, ...REQUEST_STATUS].find((s) => s.value === normalized)?.labelKey;
  const fallback = String(status || '').includes('.') ? '' : status;
  const text = labelKey ? safeLabel(labelFn, labelKey, fallback) : (fallback || normalized || '—');
  return (
    <span className={`px-2 py-1 rounded-full text-xs border ${colors[normalized] || 'bg-white/10 text-white/70 border-white/20'}`}>
      {text}
    </span>
  );
}

export const PAGE_ICONS = {
  dashboard: LayoutDashboard,
  mesApprobations: UserCheck,
  workflows: GitCompare,
  creer: FilePlus,
  chaine: Users,
  statuts: ClipboardList,
  modeles: FileText,
  analytics: BarChart3,
  config: Settings,
};

export function countByStatus(records, statusField, statusValue) {
  return records.filter((r) => normalizeStatusValue(r[statusField]) === statusValue).length;
}

/** Serialize DB/object JSON fields for modal textareas. */
export function stringifyJsonField(val, fallback = '[]') {
  if (val == null || val === '') return fallback;
  if (typeof val === 'object') return JSON.stringify(val, null, 2);
  return String(val);
}

/** Non-empty chain_json (not null, not "[]"). */
export function hasMeaningfulChainJson(record) {
  const raw = record?.chain_json;
  if (raw == null || raw === '') return false;
  const text = String(raw).trim();
  if (!text || text === '[]' || text === '{}') return false;
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed.length > 0;
    return Boolean(parsed);
  } catch {
    return text.length > 2;
  }
}

/** chain_json with more than one approval step. */
export function hasMultiStepChain(record) {
  const raw = record?.chain_json;
  if (raw == null || raw === '') return false;
  try {
    const parsed = typeof raw === 'object' ? raw : JSON.parse(String(raw));
    return Array.isArray(parsed) && parsed.length > 1;
  } catch {
    return String(raw).includes(',') && String(raw).length > 5;
  }
}

function countStatField(records, f) {
  if (typeof f.match === 'function') return records.filter(f.match).length;
  if (f.field === 'chain_json' && f.value == null) {
    return records.filter(hasMeaningfulChainJson).length;
  }
  if (f.field === 'chain_json' && f.value === 'multi') {
    return records.filter(hasMultiStepChain).length;
  }
  if (f.field === 'is_system' || f.field === 'is_default') {
    return records.filter((r) => Number(r[f.field]) === Number(f.value)).length;
  }
  return countByStatus(records, f.field, f.value);
}

export function defaultStats(records, config) {
  const { totalIcon = ShieldCheck, fields } = config;
  return sliceQawaemStats([
    { titleKey: 'stat_total', value: records.length, icon: totalIcon, color: 'from-indigo-500 to-indigo-600', variant: 'copper' },
    ...fields.map((f) => ({
      titleKey: f.titleKey,
      value: countStatField(records, f),
      icon: f.icon,
      color: f.color,
      variant: f.variant || 'forest',
    })),
  ], 4);
}

export const JSON_FORM_FIELDS = ['chain_json', 'workflow_json', 'history_json'];

/** Fired after workflow CRUD so request forms reload active workflow pickers. */
export const APPROBATIONS_WORKFLOWS_REFRESH_EVENT = 'APPROBATIONS_WORKFLOWS_REFRESH';

export function dispatchApprobationsWorkflowsRefresh() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(APPROBATIONS_WORKFLOWS_REFRESH_EVENT));
  }
}

/** Block save when reject requires a comment and notes are empty. Returns true when invalid. */
export function validateApprobationsRejectComment(config, formData, showNotify, notifyError, message) {
  if (!config?.require_comment_on_reject) return false;
  if (normalizeStatusValue(formData?.status) !== 'rejete') return false;
  if (String(formData?.notes || '').trim()) return false;
  notifyError(showNotify, message);
  return true;
}

const REQUEST_STATUS_LABELS = {
  en_attente: 'req_pending',
  en_cours: 'req_in_progress',
  approuve: 'req_approved',
  rejete: 'req_rejected',
  annule: 'req_cancelled',
};

const WORKFLOW_STATUS_LABELS = {
  actif: 'status_active',
  inactif: 'status_inactive',
  brouillon: 'status_draft',
};

/** Human-readable status for dashboard charts (Arabic fallbacks). */
export function requestStatusChartLabel(rawStatus, labelFn) {
  const normalized = normalizeStatusValue(rawStatus);
  const labelKey = REQUEST_STATUS_LABELS[normalized] || WORKFLOW_STATUS_LABELS[normalized];
  if (labelKey && labelFn) {
    return labelFn(labelKey, { defaultValue: STATUS_LABEL_FALLBACK[labelKey] || normalized });
  }
  return STATUS_LABEL_FALLBACK[labelKey] || normalized || String(rawStatus || '—');
}

export function mapChartRowsWithStatusLabels(rows = [], labelFn) {
  return rows.map((row) => ({
    ...row,
    label: requestStatusChartLabel(row.label, labelFn),
  }));
}

/** Normalize a single approval chain step (supports legacy role-only rows). */
export function normalizeChainStep(step = {}, index = 1) {
  const stepNum = step.step ?? index;
  return {
    step: stepNum,
    employee_id: step.employee_id ?? step.approver_id ?? '',
    employee_name: step.employee_name ?? step.approver_name ?? '',
    role: step.role ?? '',
  };
}

/**
 * Parse chain_json / workflow_json — accepts array or `{ steps: [...] }` template shape.
 * @returns {Array<{ step, employee_id, employee_name, role }>}
 */
export function parseChainSteps(raw) {
  if (raw == null || raw === '') return [];
  let parsed;
  try {
    parsed = typeof raw === 'object' ? raw : JSON.parse(String(raw));
  } catch {
    return [];
  }
  if (Array.isArray(parsed)) {
    return parsed.map((s, i) => normalizeChainStep(s, i + 1));
  }
  if (parsed?.steps && Array.isArray(parsed.steps)) {
    return parsed.steps.map((s, i) => normalizeChainStep(s, i + 1));
  }
  return [];
}

/** Serialize chain steps to canonical JSON array stored in chain_json / workflow_json. */
export function stringifyChainSteps(steps = []) {
  const list = Array.isArray(steps) ? steps : [];
  const normalized = list.map((s, i) => {
    const out = { step: s.step ?? i + 1 };
    if (s.employee_id != null && String(s.employee_id).trim() !== '') {
      out.employee_id = String(s.employee_id);
    }
    if (s.employee_name) out.employee_name = s.employee_name;
    if (s.role) out.role = s.role;
    return out;
  });
  return JSON.stringify(normalized, null, 2);
}

/** Templates store steps in workflow_json; workflows use chain_json — normalized to array schema. */
export function chainJsonFromTemplate(template) {
  const raw = template?.workflow_json ?? template?.chain_json;
  return stringifyChainSteps(parseChainSteps(raw));
}

export function parseApiRows(response) {
  const data = response?.data ?? response;
  return Array.isArray(data) ? data : [];
}

/** Active workflow options for request create/edit pickers; optional entity_type filter. */
export function buildActiveWorkflowOptions(rows = [], entityType = null) {
  return rows
    .filter((w) => normalizeStatusValue(w.status) === 'actif')
    .filter((w) => !entityType || !w.entity_type || w.entity_type === entityType)
    .map((w) => ({
      value: String(w.id),
      label: w.name ? `${w.name} (${w.workflow_code || w.id})` : String(w.workflow_code || w.id),
      entity_type: w.entity_type,
    }));
}

export function mapRecordJsonFields(record, emptyForm = {}) {
  const base = { ...emptyForm, ...record };
  JSON_FORM_FIELDS.forEach((key) => {
    if (key in base || record?.[key] != null) {
      base[key] = stringifyJsonField(base[key], emptyForm[key] || '[]');
    }
  });
  if (base.priority != null && base.priority !== '') {
    base.priority = String(base.priority);
  }
  return base;
}
